/**
 * Pure logic for the batch side of the unified /calculate view — no React,
 * no DOM. Extracted so it can be unit-tested without a browser or jsdom.
 *
 * The batch is assembled from individual structured requests ("bulk is nothing
 * than multiple single requests all together"), so there is no JSON parsing
 * here — items arrive already built via calculate.logic's buildSearchData.
 */
import type { GAPriceDto, GAPriceSearchData } from '../api/client.js';

/** Server cap: the BFF batch endpoint accepts at most this many items. */
export const MAX_BATCH = 50;

export type ValidateBatchResult =
  | { ok: true }
  | { ok: false; error: string };

/**
 * Validates the assembled queue before it is sent to the batch endpoint:
 * must be non-empty and within the server cap.
 */
export function validateBatch(items: readonly GAPriceSearchData[]): ValidateBatchResult {
  if (items.length === 0) {
    return { ok: false, error: 'Add at least one request to the batch.' };
  }
  if (items.length > MAX_BATCH) {
    return {
      ok: false,
      error: `Batch size exceeds ${MAX_BATCH} — the server cap is ${MAX_BATCH} items per request (got ${items.length}).`,
    };
  }
  return { ok: true };
}

export interface ItemSummary {
  mlfb: string;
  qty: number;
  l1Price: number;
  country: string;
}

/** Condensed view of a queued request, for the batch queue table. */
export function summarizeItem(item: GAPriceSearchData): ItemSummary {
  return {
    mlfb: item.ProductIdentification?.Mlfb ?? '—',
    qty: item.OrderQuantity,
    l1Price: item.L1Price,
    country: item.CustomerIdentification?.CountryCode ?? '—',
  };
}

/**
 * Interprets the per-row DTO logical outcome. Mirrors interpretResult in
 * calculate.logic.ts: the success branch carries no message (the row renders
 * the DTO fields directly), so `message` only exists when ok === false.
 * ok is true only when ResponseStatus === '200'.
 */
export function interpretRow(
  dto: GAPriceDto,
): { ok: true } | { ok: false; message: string } {
  if (dto.ResponseStatus === '200') {
    return { ok: true };
  }

  const status = dto.ResponseStatus ?? 'unknown';
  const detail = dto.ResponseMessage ?? 'No details provided';
  return { ok: false, message: `Error ${status}: ${detail}` };
}
